import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
} from "@mui/material";
import { PlusIcon } from "../components/svgicons/SvgIcons";
import FancyTooltip from "../components/utils/FancyTooltip";
import FancyPaper from "../components/FancyPaper";
import {
  getRequestListConsumption,
  getRequestProjectStatus,
} from "../services/projects/ProjectService";
import { useApp } from "../hooks/useApp";
import ResourceAccess from "../components/security/ResourceAccess";
import FancyTablePagination from "../components/utils/FancyTablePagination";
import FancySearch from "../components/utils/FancySearch";
import FancyLightButton from "../components/FancyLightButton";

interface ProjectStatusOption {
  id: number;
  name: string;
}

// Mismo listado de proyectos que usa Consumo por Proyecto (misma paginación,
// mismo filtro por texto/estado); solo cambia a dónde lleva la acción.
const ManagementProjectInventoryControl = () => {
  const { setLoading, setErrorMsg } = useApp();
  const theme = useTheme();
  const [projectsData, setProjectsData] = useState<any>([]);
  const [page, setPage] = useState(0);
  const [preFilter, setPreFilter] = useState<any>("");
  const [projectStatus, setProjectStatus] =
    useState<ProjectStatusOption | null>(null);
  const [projectStatusData, setProjectStatusData] = useState<
    ProjectStatusOption[] | null
  >(null);

  useEffect(() => {
    const dataInit = async () => {
      await handleFetchData(0, "");
      const status: any = await getRequestProjectStatus();
      if (status?.content) {
        setProjectStatusData(status.content);
      }
    };
    dataInit();
    // eslint-disable-next-line
  }, []);

  const handleFetchData = async (
    currentPage: number,
    filter: string,
    status?: any
  ) => {
    setLoading && setLoading(true);
    setProjectsData([]);
    try {
      setPage(currentPage);
      const data = await getRequestListConsumption(
        filter,
        currentPage,
        10,
        status
      );
      if (data) {
        setProjectsData(data);
      }
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
    } finally {
      setLoading && setLoading(false);
    }
  };

  const handleApplyFilter = async () => {
    await handleFetchData(0, preFilter, projectStatus?.id);
  };

  const handleStatusProjectChange = async (status: any) => {
    setProjectStatus(status);
    await handleFetchData(0, preFilter, status?.id);
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    const customPage = newPage - 1;
    setPage(customPage);
    if (customPage !== page) {
      await handleFetchData(customPage, preFilter, projectStatus?.id);
    }
  };

  const handleChangeRowsPerPage = () => {
    setPage(0);
  };

  return (
    <FancyPaper
      pagetitle="Control de inventario de proyectos"
      prevpage="Control de inventario de proyectos"
    >
      <Grid container spacing={8} sx={{ pb: 8 }}>
        <FancySearch
          preFilter={preFilter}
          setPreFilter={setPreFilter}
          handleFetchData={() => handleFetchData(0, "", projectStatus?.id)}
          handleApplyFilter={handleApplyFilter}
          handleStatusProjectChange={handleStatusProjectChange}
          projectStatusData={projectStatusData}
          projectStatus={projectStatus}
        />
      </Grid>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">{"Código"}</TableCell>
              <TableCell align="center">{"Nombre"}</TableCell>
              <TableCell align="center">{"Encargado"}</TableCell>
              <TableCell align="center">{"Estado"}</TableCell>
              <ResourceAccess isCode={true} pathOrCode={"PROJECTSTOCK:READ"}>
                <TableCell align="center">{"Acciones"}</TableCell>
              </ResourceAccess>
            </TableRow>
          </TableHead>
          <TableBody>
            {projectsData?.content?.length > 0 ? (
              projectsData.content.map((item: any) => (
                <TableRow
                  key={item.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="center">{item.code}</TableCell>
                  <TableCell align="center">{item.name}</TableCell>
                  <TableCell align="center">
                    {item.manager?.fullName}
                  </TableCell>
                  <TableCell align="center">
                    {item.status.id === 1 ? (
                      <FancyLightButton
                        backg="#3ec8d459"
                        color="#2b7076 !important"
                        text={item.status.name}
                      />
                    ) : item.status.id === 2 ? (
                      <FancyLightButton
                        backg="#c02be64a"
                        color="#c02be6!important"
                        text={item.status.name}
                      />
                    ) : item.status.id === 3 ? (
                      <FancyLightButton
                        backg="#e65f2b5e"
                        color="#E65F2B!important"
                        text={item.status.name}
                      />
                    ) : item.status.id === 4 ? (
                      <FancyLightButton
                        backg="#e1c9315c"
                        color="#7d6c00!important"
                        text={item.status.name}
                      />
                    ) : item.status.id === 5 ? (
                      <FancyLightButton
                        backg="#749e1b63"
                        color="#749e1b!important"
                        text={item.status.name}
                      />
                    ) : (
                      <FancyLightButton
                        backg="#1a932e61"
                        color="#1a932e!important"
                        text={item.status.name}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <ResourceAccess
                      isCode={true}
                      pathOrCode={"PROJECTSTOCK:READ"}
                    >
                      <FancyTooltip
                        title={"Ver inventario del proyecto"}
                        placement="top"
                      >
                        <Link
                          to={
                            "/dashboard/projectInventoryControlDetail/" +
                            item.id +
                            "/" +
                            item.name
                          }
                        >
                          <IconButton
                            aria-label="view"
                            component="label"
                            color="secondary"
                            sx={{
                              "&:hover": { color: theme.palette.secondary.dark },
                            }}
                            data-name="view"
                            data-id={item.id}
                          >
                            <PlusIcon />
                          </IconButton>
                        </Link>
                      </FancyTooltip>
                    </ResourceAccess>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5}>No existen registros</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <FancyTablePagination
        count={
          projectsData.content?.length > 0 ? projectsData.content?.length : 0
        }
        rowsPerPage={projectsData.size}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        totalElements={projectsData.totalElements}
        totalPages={projectsData.totalPages}
      />
    </FancyPaper>
  );
};

export default ManagementProjectInventoryControl;
